import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { PackageOpen } from "lucide-react";

/**
 * Dialog modal pentru execuția unui task de tip "psa_pickup_dismissed".
 * Controlat full prin props — fără stare proprie.
 */
export default function PickupTaskDialog({
  task,
  rows,
  bulkDest,
  busy,
  persons,
  locations,
  onClose,
  onSetBulkDest,
  onApplyBulk,
  onUpdateRow,
  onSubmit,
}) {
  const sourcePid = task?.meta?.source_person_id;
  const taskItems = (task?.meta?.items) || [];
  const selectedCount = Object.values(rows).filter((v) => v?.selected).length;
  const withDestCount = Object.values(rows).filter((v) => v?.selected && v?.dest).length;
  return (
    <Dialog open={!!task} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pickup-task-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-900">
            <PackageOpen size={18}/> Preia PSA de la {(task?.meta?.source_person_name) || "—"}
          </DialogTitle>
        </DialogHeader>
        <div className="bg-orange-50 border-2 border-orange-300 rounded-md p-2.5 space-y-2">
          <div className="text-xs font-bold uppercase tracking-wider text-orange-900">⚡ Destinație comună</div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-[10px] text-zinc-600 font-bold uppercase tracking-wider">Aplică aceeași destinație pe toate selectate</Label>
              <Select value={bulkDest || "__none__"} onValueChange={(v) => onSetBulkDest(v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="pickup-task-bulk-dest"><SelectValue placeholder="— Alege destinația —"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Niciuna —</SelectItem>
                  <SelectItem value="__sep_loc__" disabled className="font-bold text-blue-900 bg-blue-50">📦 Locații</SelectItem>
                  {locations.map((loc) => (<SelectItem key={`l-${loc}`} value={`loc:${loc}`}>📍 {loc}</SelectItem>))}
                  <SelectItem value="__sep_per__" disabled className="font-bold text-purple-900 bg-purple-50 mt-1">👤 Persoane</SelectItem>
                  {persons.filter((p) => p.id !== sourcePid).map((p) => (
                    <SelectItem key={`p-${p.id}`} value={`person:${p.id}`}>{p.nume} {p.prenume}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" size="sm" onClick={onApplyBulk} disabled={!bulkDest} className="h-8 text-xs bg-orange-600 hover:bg-orange-700" data-testid="pickup-task-bulk-apply">
              Aplică pe selectate
            </Button>
          </div>
        </div>
        <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1" data-testid="pickup-task-list">
          {taskItems.map((mi) => {
            const row = rows[mi.id] || {};
            return (
              <div key={mi.id} className={`p-2 rounded border ${row.selected ? "bg-red-50/40 border-red-400" : "bg-white border-zinc-200"}`} data-testid={`pickup-task-row-${mi.id}`}>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={!!row.selected}
                    onChange={(e) => onUpdateRow(mi.id, { selected: e.target.checked })}
                    className="w-4 h-4 accent-red-600 mt-1"
                    data-testid={`pickup-task-check-${mi.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap text-sm">
                      <span className="font-semibold">🦺 {mi.name}</span>
                      {mi.psa_size && <span className="px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-900 font-bold text-[10px]">măs. {mi.psa_size}</span>}
                      {mi.serial && <span className="font-mono text-[10px] text-zinc-500">SN: {mi.serial}</span>}
                      {mi.location && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-900 font-semibold text-[10px]">📍 {mi.location}</span>}
                    </div>
                    {row.selected && (
                      <div className="mt-1.5">
                        <Select value={row.dest || "__none__"} onValueChange={(v) => onUpdateRow(mi.id, { dest: v === "__none__" ? "" : v })}>
                          <SelectTrigger className="h-7 text-xs" data-testid={`pickup-task-dest-${mi.id}`}><SelectValue placeholder="— Destinație —"/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Alege destinația —</SelectItem>
                            <SelectItem value="__sep_loc__" disabled className="font-bold text-blue-900 bg-blue-50">📦 Locații</SelectItem>
                            {locations.map((loc) => (<SelectItem key={`rl-${loc}`} value={`loc:${loc}`}>📍 {loc}</SelectItem>))}
                            <SelectItem value="__sep_per__" disabled className="font-bold text-purple-900 bg-purple-50 mt-1">👤 Persoane</SelectItem>
                            {persons.filter((p) => p.id !== sourcePid).map((p) => (
                              <SelectItem key={`rp-${p.id}`} value={`person:${p.id}`}>{p.nume} {p.prenume}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2">
          <div className="text-xs text-zinc-600 mr-auto self-center">
            {selectedCount} selectate · {withDestCount} cu destinație
          </div>
          <Button variant="outline" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button
            onClick={onSubmit}
            disabled={busy || selectedCount === 0}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="pickup-task-submit"
          >
            {busy ? "Se procesează..." : `📥 Execută preluare`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
